import { Button } from "@freightsimple/ui-core/Controls/Button";
import { KeyValueCell } from "@freightsimple/ui-core/Cells/KeyValueCell";
import { StackedTextCell } from "@freightsimple/ui-core/Cells/StackedTextCell";
import { StatusDotCell } from "@freightsimple/ui-core/Cells/StatusDotCell";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { TimestampCell } from "@freightsimple/ui-core/Cells/TimestampCell";
import { asOffsetDateTime } from "@freightsimple/time/branded-types";
import { compareOffsetDateTimes } from "@freightsimple/time/helpers";
import { type InvoiceAudit } from "@freightsimple/generated-carriers-openapi-client/models/InvoiceAudit";
import { formatMoney } from "@/App/Views/carrierFormatting";
import { shipmentHref } from "@/App/carrierRoutes";

// The carrier's shipments whose invoice is under audit. Whose-turn leads the
// status column (gold = we're waiting on the carrier), and the quoted vs
// invoiced amounts sit side by side so the gap being audited is obvious.
export function InvoiceAuditsTable({ audits }: { audits: InvoiceAudit[] }) {
  const columns: Column<InvoiceAudit>[] = [
    {
      key: "ref",
      header: "Ref",
      render: (audit) => (
        <KeyValueCell
          items={[
            { key: "Pro #", value: audit.proNumber ?? undefined },
            { key: "BOL", value: audit.billOfLadingNumber ?? undefined },
          ]}
        />
      ),
    },
    {
      key: "lane",
      header: "Lane",
      render: (audit) => (
        <StackedTextCell
          primary={`${audit.pickup} → ${audit.delivery}`}
          secondary={audit.pickupDate}
        />
      ),
    },
    {
      key: "problems",
      header: "Under Review",
      render: (audit) => (
        <TextCell
          value={
            audit.problems.length > 0 ? audit.problems.join(", ") : undefined
          }
        />
      ),
    },
    {
      key: "quoted",
      header: "Quoted",
      align: "right",
      render: (audit) => (
        <TextCell
          value={
            audit.quotedCad === undefined
              ? undefined
              : formatMoney(audit.quotedCad)
          }
        />
      ),
    },
    {
      key: "invoiced",
      header: "Invoiced",
      align: "right",
      render: (audit) => (
        <TextCell
          value={
            audit.invoicedCad === undefined
              ? undefined
              : formatMoney(audit.invoicedCad)
          }
        />
      ),
    },
    {
      key: "openFor",
      header: "In Audit For",
      sortable: (a, b) =>
        compareOffsetDateTimes(
          a.openedAt ? asOffsetDateTime(a.openedAt) : undefined,
          b.openedAt ? asOffsetDateTime(b.openedAt) : undefined,
        ),
      render: (audit) => (
        <TimestampCell
          value={audit.openedAt ? asOffsetDateTime(audit.openedAt) : undefined}
          relativeOnly="age"
          relativeBadgeSize="wide"
          relativeNoSuffix
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      // Three states, not two. "With FreightSimple" used to cover everything
      // that wasn't the carrier's turn, so an audit already passed to the
      // customer was reported as ours to move.
      render: (audit) => (
        <StatusDotCell
          color={
            audit.waitingOnCarrier
              ? "gold"
              : audit.waitingOnCustomer
                ? "grey"
                : "blue"
          }
          label={
            audit.waitingOnCarrier
              ? "Waiting on you"
              : audit.waitingOnCustomer
                ? "With customer"
                : "With FreightSimple"
          }
        />
      ),
    },
  ];

  return (
    // No Panel: the page header already names this list and the count is in the
    // page's header stats, so a titled card here was a second header repeating both.
    <Table
      columns={columns}
      data={audits}
      rowKey={(audit) => audit.shipmentId}
      defaultSortKey="openFor"
      defaultSortDir="asc"
      emptyMessage="None of your shipments are in audit right now."
      rowActions={(audit) => (
        // Straight to the Invoice tab: the evidence for this audit is there,
        // and the row is an index into it rather than into the shipment.
        <Button href={shipmentHref(audit.shipmentId, "invoice")}>View</Button>
      )}
    />
  );
}
